import { ApiError } from '../api';

export type ToastMessage = {
  loading: string;
  success: string;
  info?: string;
  error: (err: ApiError) => string;
  requiredFields?: string;
  invalidExcelFile?: string;
  requiredFile?: string;
  invalidAmount?: string;
  missingInfo?: string;
  commissionTotal?: string;
};

export type ToastMessages = {
  // Generic Responses
  generic: ToastMessage;

  // File Validation
  fileValidation: ToastMessage;

  // Forms
  formValidation: ToastMessage;

  // Statements
  statements: ToastMessage;

  // Search
  search: ToastMessage;

  // Change Logs
  fetchAuditLog: ToastMessage;
  listAuditLogs: ToastMessage;
  resolveAuditLog: ToastMessage;
  resolveAuditLogs: ToastMessage;

  // Auth
  login: ToastMessage;
  logout: ToastMessage;
  updatePassword: ToastMessage;
  resetPassword: ToastMessage;
  requestPasswordReset: ToastMessage;
  sessionExpired: ToastMessage;

  // Roles
  listRoles: ToastMessage;

  // Clients
  fetchClient: ToastMessage;
  listClients: ToastMessage;
  createClient: ToastMessage;
  updateClient: ToastMessage;
  listClientReportingPeriods: ToastMessage;
  retireClient: ToastMessage;

  // Commissions
  fetchCommission: ToastMessage;
  listCommissions: ToastMessage;
  createCommission: ToastMessage;
  updateCommission: ToastMessage;

  // Dashboard
  fetchDashboard: ToastMessage;

  // Equipment
  fetchEquipment: ToastMessage;
  listEquipment: ToastMessage;
  listEquipmentCategories: ToastMessage;
  createEquipment: ToastMessage;
  updateEquipment: ToastMessage;
  retireEquipment: ToastMessage;
  deleteEquipment: ToastMessage;
  updateMultipleMeteredEquipment: ToastMessage;

  // Equipment Usages
  fetchEquipmentUsage: ToastMessage;
  listEquipmentUsages: ToastMessage;
  updateEquipmentUsage: ToastMessage;

  // Facilities
  fetchFacility: ToastMessage;
  listFacilities: ToastMessage;
  createFacility: ToastMessage;
  updateFacility: ToastMessage;
  retireFacility: ToastMessage;
  deleteFacility: ToastMessage;
  updateNonMeteredEquipment: ToastMessage;
  duplicateFacility: ToastMessage;
  duplicateFacilityName: ToastMessage;

  // FSE Registration Types
  listFSERegistrationStatuses: ToastMessage;

  // Fuel Pathways
  listFuelPathways: ToastMessage;

  // Incentive Statements
  listIncentiveStatements: ToastMessage;
  getIncentiveStatement: ToastMessage;

  // Documents
  fetchDocuments: ToastMessage;
  uploadDocument: ToastMessage;
  updateDocument: ToastMessage;
  deleteDocument: ToastMessage;
  downloadDocument: ToastMessage;
  bulkDownloadDocuments: ToastMessage;
  documentProcessing: ToastMessage;

  // Programs
  listPrograms: ToastMessage;

  // Regions
  listRegions: ToastMessage;

  // Reports
  listReportFilters: ToastMessage;
  generateReport: ToastMessage;

  // Remittance Methods
  listRemittanceMethods: ToastMessage;

  // Users
  fetchUser: ToastMessage;
  listUsers: ToastMessage;
  createUser: ToastMessage;
  updateUser: ToastMessage;
  updateEmail: ToastMessage;
  verifyEmail: ToastMessage;
  // Stripe Recipients
  createStripeRecipient: ToastMessage;
  updateStripeRecipient: ToastMessage;
  deleteStripeRecipient: ToastMessage;
  addStripeBankAccount: ToastMessage;
  moneyStripeTransfer: ToastMessage;

  // Referral Partners
  listReferralPartners: ToastMessage;
  createReferralPartner: ToastMessage;

  // Charging Networks
  listChargingNetworks: ToastMessage;
  createChargingNetwork: ToastMessage;

  // Credit Calculations
  calculateCredits: ToastMessage;
  // Api data
  listApiData: ToastMessage;
  filtersApiData: ToastMessage;
  downloadData: ToastMessage;
};

const getApiErrorMessage = (err?: ApiError): string | undefined => {
  const body = err?.body as { message?: string; errors?: Array<{ message?: string }> } | undefined;
  return body?.message || body?.errors?.[0]?.message;
};

export const toastMessages: ToastMessages = {
  generic: {
    loading: 'Loading...',
    success: 'Success.',
    error: () => 'An error has occurred.',
  },

  // File Validation
  fileValidation: {
    loading: 'Validating file...',
    success: 'File is valid.',
    error: (err: ApiError) => err?.message || 'Invalid file format or content',
    invalidExcelFile: 'Please select an Excel (.xlsx) file',
    requiredFile: 'Please select a file first',
  },

  // Statements
  statements: {
    loading: 'Loading statements...',
    success: 'Statements loaded successfully.',
    error: () => 'Could not load statements.',
  },

  // Forms
  formValidation: {
    loading: 'Validating...',
    success: 'Success.',
    error: (fieldName: unknown) => `${fieldName} is required.`,
    requiredFields: 'Please fill in all required fields.',
    commissionTotal: 'Sales and referral commissions combined must not exceed 100%',
  },

  // Search
  search: {
    loading: 'Searching...',
    success: 'Search successful.',
    error: () => 'There was an issue completing the search.',
  },

  // Change Logs
  fetchAuditLog: {
    loading: 'Loading change log...',
    success: 'Change log was loaded successfully.',
    error: () => 'Could not load change log.',
  },
  listAuditLogs: {
    loading: 'Loading change logs list...',
    success: 'Change logs list was loaded successfully.',
    error: () => 'Could not load change logs list.',
  },
  resolveAuditLog: {
    loading: 'Resolving change log...',
    success: 'Change log was marked as resolved.',
    error: () => 'An error occurred while resolving log.',
  },
  resolveAuditLogs: {
    loading: 'Resolving change logs...',
    success: 'Change logs were marked as resolved.',
    error: () => 'An error occurred while resolving logs.',
  },

  // Authentication
  login: {
    loading: 'Logging in...',
    success: 'Successfully logged in.',
    error: () => 'There was an error logging in.',
  },
  logout: {
    loading: 'Logging out...',
    success: 'Successfully logged out.',
    error: () => 'There was an error logging out.',
  },
  updatePassword: {
    loading: 'Updating password...',
    success: 'Password was updated successfully.',
    error: () => 'Could not update password.',
  },
  resetPassword: {
    loading: 'Setting new password...',
    success: 'Password was updated successfully.',
    error: () => 'Could not set new password.',
  },
  requestPasswordReset: {
    loading: 'Sending password reset email...',
    success: 'Password reset email sent successfully.',
    error: () => 'Could not send password reset email.',
  },
  sessionExpired: {
    loading: 'Session expired...',
    success: 'Your session has expired. Please login again.',
    error: () => 'Could not expire session.',
  },

  // Roles
  listRoles: {
    loading: 'Loading user roles...',
    success: 'Roles were loaded successfully.',
    error: () => 'Could not load user roles.',
  },

  // Clients
  fetchClient: {
    loading: 'Loading Client...',
    success: 'Client was loaded successfully.',
    error: () => 'Could not load Client.',
  },
  listClients: {
    loading: 'Loading Clients list...',
    success: 'Clients list was loaded successfully.',
    error: () => 'Could not load Clients list.',
  },
  createClient: {
    loading: 'Creating Client...',
    success: 'Client was created successfully.',
    error: () => 'Could not create Client.',
  },
  updateClient: {
    loading: 'Updating Client...',
    success: 'Client details updated successfully.',
    error: () => 'Could not update Client.',
  },
  listClientReportingPeriods: {
    loading: 'Loading Client Reporting Periods list...',
    success: 'Client Reporting Periods list was loaded successfully.',
    error: () => 'Could not load Client Reporting Periods list.',
  },
  retireClient: {
    loading: 'Retiring Client...',
    success: 'Client was retired successfully.',
    error: () => 'Could not retire Client.',
  },

  // Commissions
  fetchCommission: {
    loading: 'Loading Commission...',
    success: 'Commission was loaded successfully.',
    error: () => 'Could not load Commission.',
  },
  listCommissions: {
    loading: 'Loading Commissions list...',
    success: 'Commissions list was loaded successfully.',
    error: () => 'Could not load Commissions list.',
  },
  createCommission: {
    loading: 'Creating Company...',
    success: 'Company was created successfully.',
    error: () => 'Could not create Company.',
  },
  updateCommission: {
    loading: 'Updating Commission...',
    success: 'Commission was updated successfully.',
    error: () => 'Could not update Commission.',
  },

  // Dashboard
  fetchDashboard: {
    loading: 'Loading Dashboard...',
    success: 'Dashboard was loaded successfully.',
    error: () => 'Could not load Dashboard.',
  },

  // Equipment
  fetchEquipment: {
    loading: 'Loading Equipment...',
    success: 'Equipment was loaded successfully.',
    error: () => 'Could not load Equipment.',
  },
  listEquipment: {
    loading: 'Loading Equipment list...',
    success: 'Equipment list was loaded successfully.',
    error: () => 'Could not load Equipment list.',
  },
  listEquipmentCategories: {
    loading: 'Loading Equipment categories list...',
    success: 'Equipment categories were loaded successfully.',
    error: () => 'Could not load Equipment categories.',
  },
  createEquipment: {
    loading: 'Creating Equipment...',
    success: 'Equipment was created successfully.',
    error: () => 'Could not create Equipment.',
  },
  updateEquipment: {
    loading: 'Updating Equipment...',
    success: 'Equipment details updated successfully.',
    error: () => 'Could not update Equipment.',
  },
  retireEquipment: {
    loading: 'Retiring Equipment...',
    success: 'Equipment was retired successfully.',
    error: () => 'Could not retire Equipment.',
  },
  deleteEquipment: {
    loading: 'Deleting Equipment...',
    success: 'Equipment was deleted successfully.',
    error: () => 'Could not delete Equipment.',
  },
  updateMultipleMeteredEquipment: {
    loading: 'Bulk updating metered Equipment...',
    success: 'Metered Equipment was updated successfully.',
    error: () => 'An error occurred while bulk updating metered Equipment.',
  },

  // Equipment Usages
  fetchEquipmentUsage: {
    loading: 'Loading Equipment usage data...',
    success: 'Equipment usage data was loaded successfully.',
    error: () => 'Could not load Equipment usage data.',
  },
  listEquipmentUsages: {
    loading: 'Loading Equipment usages list...',
    success: 'Equipment usages list was loaded successfully.',
    error: () => 'Could not load Equipment usages list.',
  },
  updateEquipmentUsage: {
    loading: 'Updating Equipment usage data...',
    success: 'Equipment usage data was updated successfully.',
    error: () => 'Could not update Equipment usage data.',
  },

  // Facilities
  fetchFacility: {
    loading: 'Loading Facility...',
    success: 'Facility was loaded successfully.',
    error: () => 'Could not load Facility.',
  },
  listFacilities: {
    loading: 'Loading Facilities list...',
    success: 'Facilities list was loaded successfully.',
    error: () => 'Could not load Facilities list.',
  },
  createFacility: {
    loading: 'Creating Facility...',
    success: 'Facility was created successfully.',
    error: () => 'Could not create Facility.',
  },
  updateFacility: {
    loading: 'Updating Facility...',
    success: 'Facility details updated successfully.',
    error: (err: ApiError) => {
      const errors = (err as ApiError & { body?: { errors?: Array<{ field?: string; rule?: string }> } })?.body?.errors;
      if (errors?.some((e) => e.field === 'name' && e.rule === 'unique')) {
        return 'A facility with this name already exists.';
      }
      return 'Could not update Facility.';
    },
  },
  retireFacility: {
    loading: 'Retiring Facility...',
    success: 'Facility was retired successfully.',
    error: () => 'Could not retire Facility.',
  },
  deleteFacility: {
    loading: 'Deleting Facility...',
    success: 'Facility was deleted successfully.',
    error: () => 'Could not delete Facility.',
  },
  updateNonMeteredEquipment: {
    loading: 'Bulk updating non-metered Equipment...',
    success: 'Unmetered Equipment was updated successfully.',
    error: () => 'An error occurred while bulk updating non-metered Equipment.',
  },
  duplicateFacility: {
    loading: '',
    success: '',
    error: (err) =>
      `${(err.body as { program_name?: string })?.program_name} already has a Facility with this address.`,
  },
  duplicateFacilityName: {
    loading: '',
    success: '',
    error: () => 'A Facility with this name already exists.',
  },

  // FSE Registration Types
  listFSERegistrationStatuses: {
    loading: 'Loading FSE registration types list...',
    success: 'FSE registration types were loaded successfully.',
    error: () => 'Could not load FSE registration types.',
  },

  // Fuel Pathways
  listFuelPathways: {
    loading: 'Loading Fuel pathways list...',
    success: 'Fuel pathways were loaded successfully.',
    error: () => 'Could not load Fuel pathways.',
  },

  // Incentive Statements
  listIncentiveStatements: {
    loading: 'Loading Incentive Statement list...',
    success: 'Incentive Statements were loaded successfully.',
    error: () => 'Could not load Incentive Statements.',
  },
  getIncentiveStatement: {
    loading: 'Loading Incentive Statement...',
    success: 'Incentive Statement was loaded successfully.',
    error: () => 'Could not load Incentive Statement.',
  },

  // Programs
  listPrograms: {
    loading: 'Loading Programs list...',
    success: 'Programs were loaded successfully.',
    error: () => 'Could not load Programs.',
  },

  // Regions
  listRegions: {
    loading: 'Loading Regions list...',
    success: 'Regions were loaded successfully.',
    error: () => 'Could not load Regions.',
  },

  // Reports
  listReportFilters: {
    loading: 'Loading report filters...',
    success: 'Report filters loaded successfully.',
    error: (err?: ApiError) => getApiErrorMessage(err) || 'Could not load report filters.',
  },
  generateReport: {
    loading: 'Generating report...',
    success: 'Report generated successfully.',
    error: (err?: ApiError) => getApiErrorMessage(err) || 'Could not generate the report.',
  },

  // Remittance Methods
  listRemittanceMethods: {
    loading: 'Loading remittance methods list...',
    success: 'remittance methods were loaded successfully.',
    error: () => 'Could not load remittance methods.',
  },

  // Documents
  fetchDocuments: {
    loading: 'Loading documents...',
    success: 'Documents loaded successfully.',
    error: (err?: ApiError) => err?.message || 'Could not load documents.',
  },
  uploadDocument: {
    loading: 'Uploading document...',
    success: 'Document uploaded successfully.',
    error: (err?: ApiError) => {
      if (err?.status === 413) {
        return 'File is too large for document upload.';
      }
      const errors = (
        err as ApiError & {
          body?: { errors?: Array<{ rule?: string; field?: string; args?: { extnames?: unknown } }> };
        }
      )?.body?.errors;
      const extnameError = errors?.find((e) => e.rule === 'file.extname');
      if (extnameError) {
        const extnames = extnameError.args?.extnames;
        const allowed = Array.isArray(extnames) ? extnames.join(', ') : undefined;
        return allowed ? `Invalid file type. Allowed types: ${allowed}.` : 'Invalid file type.';
      }
      return err?.message || 'Could not upload document.';
    },
  },
  updateDocument: {
    loading: 'Updating document...',
    success: 'Document updated successfully.',
    error: (err?: ApiError) => err?.message || 'Could not update document.',
  },
  deleteDocument: {
    loading: 'Deleting document...',
    success: 'Document deleted successfully.',
    error: (err?: ApiError) => err?.message || 'Could not delete document.',
  },
  downloadDocument: {
    loading: 'Preparing download...',
    success: 'Document download started.',
    error: (err?: ApiError) => err?.message || 'Could not download document.',
  },
  bulkDownloadDocuments: {
    loading: 'Preparing documents...',
    success: 'Documents download started.',
    error: (err?: ApiError) => err?.message || 'Could not download documents.',
  },
  documentProcessing: {
    loading: 'Processing document...',
    success: 'Document processed successfully.',
    error: (err?: ApiError) => err?.message || 'Could not process document.',
  },

  // Users
  fetchUser: {
    loading: 'Loading User...',
    success: 'User was loaded successfully.',
    error: () => 'Could not load User.',
  },
  listUsers: {
    loading: 'Loading Users list...',
    success: 'Users list was loaded successfully.',
    error: () => 'Could not load Users list.',
  },
  createUser: {
    loading: 'Creating User...',
    success: 'User was created successfully.',
    error: () => 'Could not create User.',
  },
  updateUser: {
    loading: 'Updating User...',
    success: 'User details updated successfully.',
    error: (err: ApiError) => {
      const errors = (err as ApiError & { body?: { errors?: Array<{ field?: string; rule?: string }> } })?.body?.errors;
      if (errors?.some((e) => e.field === 'email' && e.rule === 'unique')) {
        return 'A user with this email address already exists.';
      }
      return 'Could not update User.';
    },
  },
  updateEmail: {
    loading: 'Updating email address...',
    success: 'Email address was updated successfully.',
    error: () => 'Could not update email address.',
  },
  verifyEmail: {
    loading: 'Verifying email address...',
    success: 'Email address was successfully verified. You may now log in with this email.',
    error: () => 'There was an error verifying this email address.',
  },
  // Stripe Recipients
  createStripeRecipient: {
    loading: 'Creating Stripe Recipient...',
    success: 'Stripe Recipient was created successfully.',
    error: () => 'Could not create Stripe Recipient.',
  },
  updateStripeRecipient: {
    loading: 'Updating Stripe Recipient...',
    success: 'Stripe Recipient was updated successfully.',
    error: () => 'Could not update Stripe Recipient.',
  },
  deleteStripeRecipient: {
    loading: 'Deleting Stripe Recipient...',
    success: 'Stripe Recipient was deleted successfully.',
    error: () => 'Could not delete Stripe Recipient.',
  },
  addStripeBankAccount: {
    loading: 'Adding Stripe Bank Account...',
    success: 'Bank Account was added successfully.',
    error: () => 'Could not add Bank Account.',
  },
  moneyStripeTransfer: {
    loading: 'Processing money transfer...',
    success: 'Money sent successfully!',
    error: (err: ApiError) => `Could not send money: ${err?.message || 'An unknown error occurred'}`,
    invalidAmount: 'Please enter a valid positive amount.',
    missingInfo: 'Please select an account, ensure bank account is loaded, and enter an amount.',
  },

  // Referral Partners
  listReferralPartners: {
    loading: 'Loading Referral Partners list...',
    success: 'Referral Partners list was loaded successfully.',
    error: () => 'Could not load Referral Partners list.',
  },
  createReferralPartner: {
    loading: 'Creating Referral Partner...',
    success: 'Referral Partner was created successfully.',
    error: (err: ApiError) => {
      const errors = (err as ApiError & { body?: { errors?: Array<{ message?: string }> } })?.body?.errors;
      if (errors?.[0]?.message) {
        return 'Referral Partner already exists.';
      }
      return err?.message || 'Could not create Referral Partner.';
    },
    requiredFields: 'Partner name is required.',
  },

  // Charging Networks
  listChargingNetworks: {
    loading: 'Loading Charging Networks list...',
    success: 'Charging Networks list was loaded successfully.',
    error: () => 'Could not load Charging Networks list.',
  },
  createChargingNetwork: {
    loading: 'Creating Charging Network...',
    success: 'Charging Network was created successfully.',
    error: (err: ApiError) => {
      const errors = (err as ApiError & { body?: { errors?: Array<{ message?: string }> } })?.body?.errors;
      if (errors?.[0]?.message) {
        return 'Charging Network already exists.';
      }
      return err?.message || 'Could not create Charging Network.';
    },
    requiredFields: 'Network name is required.',
  },

  // Credit Calculations
  calculateCredits: {
    loading: 'Starting credit calculation...',
    success: 'Credit calculation started.',
    error: (err: ApiError) => (err?.body as { message?: string })?.message ?? 'Could not start the credit calculation.',
  },
  // Api Data
  listApiData: {
    loading: 'Loading Api Data...',
    success: 'Api Data was loaded successfully.',
    error: () => 'Could not load Api Data.',
  },
  filtersApiData: {
    loading: 'Loading Api Data Filters...',
    success: 'Api Data Filters was loaded successfully.',
    error: () => 'Could not load Api Data Filters.',
  },
  downloadData: {
    loading: 'Preparing download...',
    success: 'Document download started.',
    error: (err?: ApiError) => err?.message || 'Could not download document.',
  },
};
